import { ChangeDetectionStrategy, Component, ElementRef, inject, signal, Signal, viewChild, WritableSignal, OnInit, Input, HostBinding, InputSignal, input, forwardRef } from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { NgControl, ControlValueAccessor, FormControl, ReactiveFormsModule, Validators } from '@angular/forms';
import { LocationService } from '@core/services/location/locations.service';
import { ApiCollectionResponse } from '@shared/types/api/api-collection-response';
import { isEqual, isNull, isString } from 'es-toolkit';
import { BehaviorSubject, debounceTime, distinctUntilChanged, Observable, Subject, throttleTime } from 'rxjs';
import { MatFormFieldControl } from '@angular/material/form-field';
import { TranslationStoreService } from '@ogs-gmbh/ngx-translate';
import { MatAutocompleteModule, MatAutocompleteOrigin } from '@angular/material/autocomplete';
import { RegionDto } from '@core/types/common.types';
import { MatInputModule } from '@angular/material/input';
import { IntersectionObserverDirective } from '@shared/directives/intersection-observer/intersection-observer.directive';
import { ObserveDirective } from '@shared/directives/intersection-observer/observe.directive';
import { RegionSelectChange } from './types/region-select-change.type';
import { RegionSelectTouch } from './types/region-select-touch.type';


const CONTROL_TYPE: string = "region-select";

@Component({
  selector: 'app-region-select',
  standalone: true,
  imports: [ MatInputModule, ReactiveFormsModule, MatAutocompleteModule, IntersectionObserverDirective, ObserveDirective ],
  providers: [
    {
      provide: MatFormFieldControl,
      useExisting: forwardRef(() => RegionSelectComponent)
    }
  ],
  templateUrl: './region-select.component.html',
  styleUrl: './region-select.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class RegionSelectComponent implements ControlValueAccessor, MatFormFieldControl<RegionDto | null>, OnInit {
  private readonly _translationStoreService: TranslationStoreService = inject(TranslationStoreService);

  public readonly countryIsoCodeTwo: InputSignal<string | null> = input<string | null>(null);

  public readonly controlType: string = CONTROL_TYPE;

  public readonly ngControl: NgControl | null = inject(NgControl, {
    optional: true,
    self: true
  });

  // TODO dont require access connected within component
  public readonly connectedFormField: InputSignal<MatAutocompleteOrigin> = input.required();

  protected readonly regionControl: FormControl<RegionDto | null> = new FormControl<RegionDto | null>(null);

  private readonly _locationService: LocationService = inject(LocationService);

  private readonly _inputElement: Signal<ElementRef<HTMLInputElement>> = viewChild.required("regionInput");

  private readonly _searchTermChange$: BehaviorSubject<string> = new BehaviorSubject<string>("");

  // eslint-disable-next-line @unicorn/no-useless-undefined
  protected readonly selectableCountries: WritableSignal<ApiCollectionResponse<RegionDto> | undefined> = signal(undefined);

  public get value (): RegionDto | null {
    return this.regionControl.value;
  }

  private _stateChange$: Subject<void> = new Subject<void>();

  // Violates ogs-naming convention but needs to be implemented by form-field-interface
  public stateChanges: Observable<void> = this._stateChange$.asObservable();

  public static nextId: number = 0;

  @HostBinding() id: string = `${ CONTROL_TYPE }-${ RegionSelectComponent.nextId++ }`;

  @Input()
  public set placeholder (placeholder: string) {
    this._placeholder = placeholder;
    this._stateChange$.next();
  }

  public get placeholder (): string {
    return this._placeholder;
  }

  private _placeholder: string = "";

  private _touched: boolean = false;

  private _focused: boolean = false;

  public get focused (): boolean {
    return this._focused;
  }

  public get empty (): boolean {
    return !this.regionControl.value;
  }

  @HostBinding('class.floating')
  public get shouldLabelFloat (): boolean {
    return this.focused || !this.empty;
  }

  @Input()
  public set required (isRequired: boolean) {
    this._required = isRequired;
    this._stateChange$.next();
  }

  public get required (): boolean {
    // eslint-disable-next-line @tseslint/unbound-method
    return this._required || Boolean(this.ngControl?.control?.hasValidator(Validators.required));
  }

  private _required: boolean = false;

  public get disabled (): boolean {
    return this.regionControl.disabled;
  }

  public get errorState (): boolean {
    return this.regionControl.invalid && this._touched;
  }

  autofilled?: boolean;

  userAriaDescribedBy?: string;

  disableAutomaticLabeling?: boolean;

  private _onChange: RegionSelectChange = undefined;

  private _onTouch: RegionSelectTouch = undefined;


  constructor () {
    if (this.ngControl)
      this.ngControl.valueAccessor = this;

    toObservable(this.countryIsoCodeTwo)
      .pipe(
        takeUntilDestroyed()
      )
      .subscribe(() => {
        this.loadRegions(true);
        this.regionControl.setValue(null, { emitEvent: false });
      });
    this.regionControl.valueChanges
      .pipe(
        takeUntilDestroyed()
      )
      .subscribe((value: RegionDto | null) => {
        this._stateChange$.next();

        if (this._onChange)
          this._onChange(value);
      });
    this._searchTermChange$.pipe(
      takeUntilDestroyed(),
      debounceTime(400),
      distinctUntilChanged()
    ).subscribe(() => {
      this._stateChange$.next();
      this.loadRegions(true);
    });
    this._translationStoreService.getLocale$()
      .pipe(
        throttleTime(1000, undefined, { leading: true, trailing: true }),
        distinctUntilChanged(isEqual),
        takeUntilDestroyed()
      )
      .subscribe(() => {
        this.loadRegions(true);
      });
  }


  public setDescribedByIds (ids: string[]): void {
    this._inputElement().nativeElement.setAttribute('aria-describedby', ids.join(' '));
  }

  public onContainerClick (mouseEvent: MouseEvent): void {
    if ((mouseEvent.target as Element).tagName.toLowerCase() !== 'input')
      this._inputElement().nativeElement.focus();
  }

  public ngOnInit (): void {
    this.loadRegions();
  }


  protected searchRegion (): void {
    const searchTerm: string = this._inputElement().nativeElement.value;

    this._searchTermChange$.next(searchTerm);
  }


  // eslint-disable-next-line @tseslint/class-methods-use-this
  protected displayRegion = (region: RegionDto | string | null): string => (isString(region) || isNull(region)
    ? region ?? ""
    : region.name);


  protected loadRegions (reset?: boolean): void {
    const selectableCountries: ApiCollectionResponse<RegionDto> | undefined = this.selectableCountries();
    const currentCountryIsoCodeTwo: string | null = this.countryIsoCodeTwo();

    if (!currentCountryIsoCodeTwo || !(reset || selectableCountries === undefined || selectableCountries.totalCount > selectableCountries.data.length))
      return;

    this._locationService.getRegions(currentCountryIsoCodeTwo, {
      offset: reset || !selectableCountries?.data.length ? 0 : selectableCountries.data.length,
      searchTerm: this._searchTermChange$.getValue() === "" ? undefined : this._searchTermChange$.getValue()
    })
      .subscribe((response: ApiCollectionResponse<RegionDto>) => {
        this.selectableCountries.set({
          totalCount: response.totalCount,
          data: reset ? response.data : [ ...selectableCountries?.data ?? [], ...response.data ]
        });
      });
  }

  protected setFocusIn (): void {
    if (!this.focused) {
      this._focused = true;
      this._stateChange$.next();
    }
  }

  protected setFocusOut (focusEvent: FocusEvent): void {
    this._searchTermChange$.next("");

    if (!this._inputElement().nativeElement.contains(focusEvent.relatedTarget as Element)) {
      this._focused = false;
      this.markTouched();
      this._stateChange$.next();
    }
  }

  protected markTouched (): void {
    this._touched = true;

    if (this._onTouch)
      this._onTouch();
  }

  public writeValue (value: RegionDto | null): void {
    this.regionControl.setValue(value, { emitEvent: false });
    this._stateChange$.next();
  }

  public registerOnChange (callback: RegionSelectChange): void {
    this._onChange = callback;
  }

  registerOnTouched (callback: RegionSelectTouch): void {
    this._onTouch = callback;
  }

  setDisabledState? (isDisabled: boolean): void {
    if (isDisabled)
      this.regionControl.disable({ emitEvent: false });
    else
      this.regionControl.enable({ emitEvent: false });
  }
}
